import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { priceForecastService } from "@/services/priceForecastService";
import { Brain, Loader2 } from "lucide-react";
import { format, subMonths } from "date-fns";
import { getCanonicalProductNames } from "@/services/benchmark/productNameMapping";

const LOCATION_GROUPS = [
  { id: "farm", label: "Farm Prices" },
  { id: "distribution", label: "Distribution Centers" },
  { id: "local_shop", label: "Local Shops" },
  { id: "supermarket", label: "Supermarkets" },
  { id: "ecommerce", label: "E-commerce" },
  { id: "sunday_market", label: "Sunday Markets" },
  { id: "chipchip", label: "ChipChip" },
];

export default function ModelTraining() {
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedLocations, setSelectedLocations] = useState<string[]>(["farm", "distribution", "local_shop", "chipchip"]);
  const [isTraining, setIsTraining] = useState(false);
  const [trainingResults, setTrainingResults] = useState<any>(null);
  const { toast } = useToast();
  const canonicalProducts = getCanonicalProductNames().sort();

  const handleLocationToggle = (locationId: string) => {
    setSelectedLocations(prev =>
      prev.includes(locationId)
        ? prev.filter(id => id !== locationId)
        : [...prev, locationId]
    );
  };

  const handleTrain = async () => {
    if (!selectedProduct) {
      toast({
        title: "Product Required",
        description: "Please select a product to train",
        variant: "destructive",
      });
      return;
    }

    if (selectedLocations.length === 0) {
      toast({
        title: "Locations Required",
        description: "Please select at least one location group",
        variant: "destructive",
      });
      return;
    }

    setIsTraining(true);
    setTrainingResults(null);

    try {
      // Use last 18 months for training
      const endDate = format(new Date(), 'yyyy-MM-dd');
      const startDate = format(subMonths(new Date(), 18), 'yyyy-MM-dd');

      const result = await priceForecastService.trainModel(
        selectedProduct,
        selectedLocations,
        startDate,
        endDate
      );

      setTrainingResults(result);

      toast({
        title: "Training Complete",
        description: `Successfully trained ${result.models.length} models`,
      });
    } catch (error: any) {
      console.error('Training error:', error);
      toast({
        title: "Training Failed",
        description: error.message || "Failed to train models",
        variant: "destructive",
      });
    } finally {
      setIsTraining(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5" />
            Train New Model
          </CardTitle>
          <CardDescription>
            Train machine learning models to predict future prices based on historical data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Product</Label>
            <Select value={selectedProduct} onValueChange={setSelectedProduct}>
              <SelectTrigger>
                <SelectValue placeholder="Select a product" />
              </SelectTrigger>
              <SelectContent>
                {canonicalProducts.map(product => (
                  <SelectItem key={product} value={product}>
                    {product}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3">
            <Label>Location Groups</Label>
            <div className="grid grid-cols-2 gap-3">
              {LOCATION_GROUPS.map((location) => (
                <div key={location.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={location.id}
                    checked={selectedLocations.includes(location.id)}
                    onCheckedChange={() => handleLocationToggle(location.id)}
                  />
                  <label
                    htmlFor={location.id}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {location.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-4">
            <Button
              onClick={handleTrain}
              disabled={isTraining || !selectedProduct || selectedLocations.length === 0}
              className="w-full"
            >
              {isTraining && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isTraining ? "Training Models..." : "Train Models"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {trainingResults && (
        <Card>
          <CardHeader>
            <CardTitle>Training Results</CardTitle>
            <CardDescription>
              Model performance metrics
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {trainingResults.models.map((model: any) => (
                <Card key={model.model_id}>
                  <CardHeader>
                    <CardTitle className="text-base">{model.location_group}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <div className="text-muted-foreground">MAE</div>
                        <div className="font-semibold">{model.performance.mae?.toFixed(2) ?? 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">MAPE</div>
                        <div className="font-semibold">{model.performance.mape?.toFixed(2) ?? 'N/A'}%</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">R²</div>
                        <div className="font-semibold">{model.performance.r_squared?.toFixed(3) ?? 'N/A'}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
